import React, { useCallback } from 'react';
import { t } from '@lingui/macro';

import ImageColumn from '@rover/components/src/cards/SearchResultCard/ImageColumn';
import { Box, Button, Flex, Link, Text } from '@rover/kibble/core';
import { Badge } from '@rover/kibble/official-patterns/Badge';
import Divider from '@rover/kibble/patterns/Divider';
import StarRating from '@rover/kibble/patterns/StarRating';
import { SearchCardTreatment } from '@rover/types/src/SearchCardTreatment';

import type { NameSearchResult } from '../types';

type Props = {
  result: NameSearchResult;
  onCardClick?: () => void;
};

const getDisplayName = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return fullName;
  const firstName = parts[0];
  const lastInitial = parts[parts.length - 1]?.[0] ?? '';
  return `${firstName} ${lastInitial}.`;
};

const SearchByNameSitterCard = ({ result, onCardClick }: Props): JSX.Element => {
  const {
    personName,
    shortName,
    avatarUrl,
    ratingsAverage,
    reviewsCount,
    profileUrl,
    isPreviouslyBooked,
  } = result;
  const displayName = getDisplayName(personName);
  const safeRating = Number.isFinite(ratingsAverage) ? (ratingsAverage as number) : 0;
  const hasReviews = (reviewsCount ?? 0) > 0;

  const handleClick = useCallback((): void => {
    onCardClick?.();
  }, [onCardClick]);

  return (
    <Box testID={`search-by-name-sitter-card-${result.personOpk}`}>
      <Box py="4x">
        <Flex gap="3x" alignItems="flex-start">
          <ImageColumn
            displayExpanded={false}
            desktopProfileImage={avatarUrl?.medium ?? ''}
            mobileProfileImage={avatarUrl?.small ?? ''}
            shortName={shortName || displayName}
            cardTreatment={SearchCardTreatment.legacy}
          />
          <Box flex="1">
            <Flex justifyContent="space-between" alignItems="center" mb="1x">
              <Text size="200" fontWeight="semibold" textColor="primary" as="h4">
                {displayName}
              </Text>
              {hasReviews && (
                <Flex
                  alignItems="center"
                  gap="1x"
                  webProps={{
                    'aria-label': t`Rated ${safeRating} out of 5 from ${reviewsCount} reviews`,
                  }}
                >
                  <StarRating
                    rating={safeRating}
                    variant="compact"
                    textSize="100"
                    fontWeight="semibold"
                  />
                  <Text size="100" textColor="secondary">
                    ({reviewsCount})
                  </Text>
                </Flex>
              )}
            </Flex>
            {isPreviouslyBooked && (
              <Box mb="2x">
                <Badge variant="info" text={t`Previously booked`} />
              </Box>
            )}
          </Box>
        </Flex>
        <Flex justifyContent="center" alignItems="center" gap="4x" mt="4x">
          <Link
            href={profileUrl}
            onPress={handleClick}
            variant="primary"
            size="200"
            underline={false}
            webProps={{ 'aria-label': t`View Profile, ${displayName}` }}
            testID={`search-by-name-view-profile-${result.personOpk}`}
          >
            {t`View Profile`}
          </Link>
          <Button
            variant="primary"
            size="small"
            href={`${profileUrl}contact/`}
            onClick={handleClick}
            webProps={{ 'aria-label': t`Send Intro Message to ${displayName}` }}
            testID={`search-by-name-send-message-${result.personOpk}`}
          >
            {t`Send Intro Message`}
          </Button>
        </Flex>
      </Box>
      <Divider />
    </Box>
  );
};

export default SearchByNameSitterCard;
